import type {
  EscalationTarget,
  Leave,
  LeaveCreate,
  LeaveDetailPayload,
  LeaveListItem,
  LeaveRuleResult,
  LeaveStatus,
  WindowRecommendationResponse,
} from '@lms/shared-types'
import {
  leaveDetailPayloadSchema,
  windowRecommendationResponseSchema,
} from '@lms/shared-types'
import { ApiError, apiRequest, apiRequestPaged, type PagedResult } from './client'

export interface ListLeavesQuery {
  scope?: 'team' | 'all'
  status?: LeaveStatus
  page?: number
  limit?: number
  /** Cursor: ISO datetime — return rows with created_at < this. Backend
   *  populates meta.hasMore + meta.nextCursor when this is supplied. */
  before?: string | null
}

export async function getLeaveDetail(id: string): Promise<LeaveDetailPayload> {
  const raw = await apiRequest<unknown>(
    `/api/v1/leaves/${encodeURIComponent(id)}`,
  )
  return leaveDetailPayloadSchema.parse(raw)
}

function buildLeavesQuery(query: ListLeavesQuery): string {
  const params = new URLSearchParams()
  if (query.scope) params.set('scope', query.scope)
  if (query.status) params.set('status', query.status)
  if (query.before) params.set('before', query.before)
  if (query.page != null) params.set('page', String(query.page))
  if (query.limit != null) params.set('limit', String(query.limit))
  return params.toString()
}

/** Legacy flat-list caller — returns the raw array for callers that don't
 *  need pagination metadata (small counts). */
export function listLeaves(query: ListLeavesQuery = {}): Promise<LeaveListItem[]> {
  const qs = buildLeavesQuery(query)
  return apiRequest<LeaveListItem[]>(`/api/v1/leaves${qs ? `?${qs}` : ''}`)
}

/** Paginated leaves list — use this for manager/HR queue surfaces that drive
 *  usePagedList numbered pagination. Passes ?page= offset for numbered pages;
 *  the endpoint also still accepts a ?before= cursor for any legacy caller. */
export function listLeavesPage(
  query: ListLeavesQuery = {},
): Promise<PagedResult<LeaveListItem>> {
  const qs = buildLeavesQuery(query)
  return apiRequestPaged<LeaveListItem>(`/api/v1/leaves${qs ? `?${qs}` : ''}`)
}

/** Leaves the current approver personally escalated up the chain — the
 *  Approvals "You escalated" tab. Sourced from the audit log server-side
 *  (the leave row records only the escalation target, not the escalating
 *  actor), so it has its own endpoint rather than a status filter on the
 *  leaves list. Terminal approvers (Exec Director) get an empty list. */
export function listEscalatedByMe(limit = 50): Promise<LeaveListItem[]> {
  return apiRequest<LeaveListItem[]>(
    `/api/v1/leaves/escalated-by-me?limit=${limit}`,
  )
}

// Shape of the 409 body the /approve route returns when a leave is over
// capacity and the manager hasn't confirmed yet. Drives the over-capacity
// confirm dialog. See services/api/src/routes/leaves.py (capacity_ack_required)
// and isCapacityAckRequired below.
export interface CapacityAckSnapshot {
  peakOthers: number
  teamCap: number
  day: string
  capPhrase: string
}

/** Narrows an unknown error to the /approve `capacity_ack_required` 409 and
 *  returns its snapshot; null for any other error. The web catches this to
 *  raise the over-capacity confirm dialog, then re-calls approveLeave with
 *  `overCapacityAck: true`. */
export function capacityAckRequired(err: unknown): CapacityAckSnapshot | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null
  const detail = (err.details as { detail?: unknown } | null)?.detail
  if (
    typeof detail !== 'object' ||
    detail === null ||
    (detail as { code?: unknown }).code !== 'capacity_ack_required'
  ) {
    return null
  }
  const cap = (detail as { capacity?: unknown }).capacity
  if (typeof cap !== 'object' || cap === null) return null
  const c = cap as Record<string, unknown>
  return {
    peakOthers: Number(c.peakOthers ?? 0),
    teamCap: Number(c.teamCap ?? 0),
    day: String(c.day ?? ''),
    capPhrase: String(c.capPhrase ?? ''),
  }
}

export function approveLeave(
  id: string,
  reason?: string,
  opts?: { overCapacityAck?: boolean },
): Promise<Leave> {
  // Reason is optional at the API level so routine manager approvals stay
  // zero-friction. Director-tier blackout overrides supply a 10+ char reason
  // for audit-committee defensibility (Rajesh §1.1).
  //
  // `overCapacityAck` is set only after the manager confirms the over-capacity
  // dialog (capacity de-gated 2026-09-24). Without it, the server 409s with a
  // `capacity_ack_required` snapshot (see capacityAckRequired) when the leave is
  // over cap, so the UI can raise the dialog; with it, the approval goes through
  // and the leave is flagged + audited + surfaced on the Slack audit line.
  const body: Record<string, unknown> = {}
  if (reason) body.reason = reason
  if (opts?.overCapacityAck) body.overCapacityAck = true
  return apiRequest<Leave>(`/api/v1/leaves/${encodeURIComponent(id)}/approve`, {
    method: 'POST',
    body,
  })
}

export function addLeaveNote(leaveId: string, note: string): Promise<{ ok: boolean }> {
  return apiRequest<{ ok: boolean }>(`/api/v1/leaves/${encodeURIComponent(leaveId)}/note`, {
    method: 'POST',
    body: { note },
  })
}

export function rejectLeave(id: string, reason: string): Promise<Leave> {
  return apiRequest<Leave>(`/api/v1/leaves/${encodeURIComponent(id)}/reject`, {
    method: 'POST',
    body: { reason },
  })
}

/**
 * Wave 5 (2026-05-20) — POST `/api/v1/leaves/{id}/decision/revoke`.
 * Reverses the calling actor's most recent approve within the
 * 60s server-enforced window. 410 once the window closes.
 */
export class RevocationWindowClosedError extends Error {
  constructor() {
    super('gone_revocation_window')
    this.name = 'RevocationWindowClosedError'
  }
}

export async function revokeApproveDecision(id: string): Promise<Leave> {
  try {
    return await apiRequest<Leave>(
      `/api/v1/leaves/${encodeURIComponent(id)}/decision/revoke`,
      { method: 'POST', body: {} },
    )
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : String(err)
    if (msg.includes('gone_revocation_window') || msg.includes('410')) {
      throw new RevocationWindowClosedError()
    }
    throw err
  }
}

export function escalateLeave(id: string, target: EscalationTarget): Promise<Leave> {
  return apiRequest<Leave>(`/api/v1/leaves/${encodeURIComponent(id)}/escalate`, {
    method: 'POST',
    body: { target },
  })
}

export function previewLeave(input: LeaveCreate): Promise<LeaveRuleResult> {
  return apiRequest<LeaveRuleResult>('/api/v1/leaves/preview', {
    method: 'POST',
    body: input,
  })
}

/**
 * Re-runs the rule engine against a persisted leave. Used by the manager
 * Impact preview surfaces — warnings aren't stored on the leave row, so
 * this rebuilds them from current team state at view time.
 */
export function previewExistingLeave(leaveId: string): Promise<LeaveRuleResult> {
  return apiRequest<LeaveRuleResult>(
    `/api/v1/leaves/${encodeURIComponent(leaveId)}/preview`,
    { method: 'POST' },
  )
}

/** Zone 4 (Requester View) data envelope.
 *
 * Pairs the submit-time rule-engine snapshot with a fresh
 * re-evaluation so the approver can see what the requester was told
 * and whether the situation has changed. Source-of-truth principle:
 * the api builds the verbatim `requesterViewText` so the FE never
 * paraphrases. See
 * artifacts/agent-conversation-showcase/INSTRUCTIONS.md § 14. */
export interface RequesterView {
  leaveId: string
  submitSnapshot: Record<string, unknown> | null
  currentEvaluation: LeaveRuleResult
  deltaDetected: boolean
  requesterViewText: string
}

export function fetchRequesterView(leaveId: string): Promise<RequesterView> {
  return apiRequest<RequesterView>(
    `/api/v1/leaves/${encodeURIComponent(leaveId)}/requester-view`,
    { method: 'GET' },
  )
}

/** ILMU-generated title + subtitle for the LeaveImpactCard.
 *
 * Phase 4 of the decision-surface redesign. The card historically
 * rendered a deterministic pair driven by overall severity
 * ("Approve. No conditions." / "No conditions raised by the rule
 * engine.") — flagged as prescriptive. The api proxies to ai-service
 * which returns factual prose: no directives, no verdict adjectives,
 * no numeric balance figures. The web wraps the rendered text in
 * &lt;AiOwnedText&gt; so the verdict-language linter scans it. */
export interface ImpactHeadline {
  headline: string
  subtitle: string
  invocationId: string
}

export interface CreateLeaveResponse {
  leave: Leave
  preview: LeaveRuleResult
}

export function createLeave(input: LeaveCreate): Promise<CreateLeaveResponse> {
  return apiRequest<CreateLeaveResponse>('/api/v1/leaves', {
    method: 'POST',
    body: input,
  })
}

/** Cancel an in-flight (pending / escalated) leave the caller owns. */
export function cancelLeave(id: string): Promise<Leave> {
  return apiRequest<Leave>(`/api/v1/leaves/${encodeURIComponent(id)}/cancel`, {
    method: 'POST',
  })
}

/**
 * Request cancellation of an already-APPROVED leave the caller owns. The
 * leave moves to `pending_cancellation` and routes to the manager; a short
 * `reason` is required (shown on the manager's decision card).
 */
export function requestLeaveCancellation(id: string, reason: string): Promise<Leave> {
  return apiRequest<Leave>(
    `/api/v1/leaves/${encodeURIComponent(id)}/request-cancellation`,
    { method: 'POST', body: { reason } },
  )
}

/** Manager approves a pending cancellation request → leave is cancelled. */
export function approveCancellation(id: string): Promise<Leave> {
  return apiRequest<Leave>(
    `/api/v1/leaves/${encodeURIComponent(id)}/approve-cancellation`,
    { method: 'POST' },
  )
}

/** Manager rejects a pending cancellation request → leave stays approved. */
export function rejectCancellation(id: string, reason?: string): Promise<Leave> {
  return apiRequest<Leave>(
    `/api/v1/leaves/${encodeURIComponent(id)}/reject-cancellation`,
    { method: 'POST', ...(reason ? { body: { reason } } : {}) },
  )
}

// ── RFC 004 §4.2 — Blackout-after-approval manager recommendation ────────────
//
// When HR declares a blackout that overlaps an already-approved annual leave,
// the manager can file a recommendation for the Director. Guarded server-side
// by status + window-deadline checks; the FE just renders the affordance.

/** POST /api/v1/leaves/{id}/manager-recommendation — manager files note. */
export function blackoutManagerRecommendation(
  leaveId: string,
  recommendation: string,
): Promise<Leave> {
  return apiRequest<Leave>(
    `/api/v1/leaves/${encodeURIComponent(leaveId)}/manager-recommendation`,
    { method: 'POST', body: { recommendation } },
  )
}

// ── Ask-HOD for input (RFC 002, Slice 4) ─────────────────────────────────────

export interface AskHodBody {
  note?: string | undefined
}

export interface AskHodResponse {
  status: string
  hodCount: string
}

/**
 * POST `/api/v1/leaves/{leaveId}/ask-hod` — sends the HOD a Slack DM with
 * the leave details and an optional manager note. The HOD gets a deep-link
 * back to the manager's triage view. Leave status is not mutated.
 */
export function askHodForInput(
  leaveId: string,
  body: AskHodBody = {},
): Promise<AskHodResponse> {
  return apiRequest<AskHodResponse>(
    `/api/v1/leaves/${encodeURIComponent(leaveId)}/ask-hod`,
    { method: 'POST', body },
  )
}

/**
 * DELETE `/api/v1/leaves/:id` — apply-flow rollback.
 *
 * Hard-deletes a freshly-created pending leave when the document upload
 * step fails post-creation. Owner-only, pending/escalated only.
 * Cascade-cleans uploaded documents (S3 best-effort).
 */
export function rollbackLeave(
  id: string,
): Promise<{ id: string; rolledBack: boolean; documentCount: number }> {
  return apiRequest(`/api/v1/leaves/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  })
}

// ── HR cancel approved leave (slice B2) ─────────────────────────────────────

/**
 * POST `/api/v1/leaves/:id/hr-cancel` — HR-only.
 * Voids an already-approved leave and refunds the taken days to balance.
 * Reason must be at least 10 characters.
 */
export function cancelHrLeave(leaveId: string, reason: string): Promise<Leave> {
  return apiRequest<Leave>(`/api/v1/leaves/${encodeURIComponent(leaveId)}/hr-cancel`, {
    method: 'POST',
    body: { reason },
  })
}

/**
 * POST `/api/v1/leaves/window-recommendations` — deterministic suggestion of
 * up to 3 alternative lower-impact windows of the same length. No LLM; team
 * is derived from the actor server-side.
 *
 * ``leaveType`` is required for balance-aware analysis (§1.6 overflow,
 * advance-booking cap, 70% early-utilisation advisory).
 */
export async function fetchWindowRecommendations(
  startDate: string,
  endDate: string,
  leaveType: string = '',
  includeAlternatives: boolean = true,
): Promise<WindowRecommendationResponse> {
  const raw = await apiRequest<unknown>('/api/v1/leaves/window-recommendations', {
    method: 'POST',
    body: { startDate, endDate, leaveType, includeAlternatives },
  })
  return windowRecommendationResponseSchema.parse(raw)
}

// ── Employee cancel-draft SSE stream ────────────────────────────────────────

export type CancelDraftTone = 'casual' | 'brief' | 'apologetic'

export interface CancelDraftProvenance {
  model: string
  latency: number
  source: string
}

export type CancelDraftEvent =
  | { kind: 'status'; action: string }
  | { kind: 'content'; text: string }
  | { kind: 'provenance'; provenance: CancelDraftProvenance }
  | { kind: 'error'; message: string }
  | { kind: 'done' }

function parseCancelDraftLine(raw: string): CancelDraftEvent | null {
  if (raw === '[DONE]') return { kind: 'done' }
  let parsed: { type?: string; data?: unknown; choices?: Array<{ delta?: { content?: string } }> }
  try {
    parsed = JSON.parse(raw) as typeof parsed
  } catch {
    return null
  }
  if (parsed.type === 'status' && parsed.data && typeof parsed.data === 'object') {
    const action = (parsed.data as { action?: unknown }).action
    return { kind: 'status', action: typeof action === 'string' ? action : 'unknown' }
  }
  if (parsed.type === 'provenance' && parsed.data && typeof parsed.data === 'object') {
    const d = parsed.data as { model?: unknown; latency?: unknown; source?: unknown }
    return {
      kind: 'provenance',
      provenance: {
        model: typeof d.model === 'string' ? d.model : 'unknown',
        latency: typeof d.latency === 'number' ? d.latency : 0,
        source: typeof d.source === 'string' ? d.source : 'cancel-draft',
      },
    }
  }
  if (parsed.type === 'error' && parsed.data && typeof parsed.data === 'object') {
    const d = parsed.data as { body?: unknown; status?: unknown }
    return {
      kind: 'error',
      message: typeof d.body === 'string' ? d.body : `HTTP ${String(d.status ?? 500)}`,
    }
  }
  if (parsed.choices && parsed.choices[0]?.delta?.content) {
    return { kind: 'content', text: parsed.choices[0].delta.content }
  }
  return null
}
